export const agents = {
  title: "Flows",
  subtitle: "Enhance your agile processes",
  description: "Manage the flows of this workspace",
  moreIntegrations: "+{{count}} more tools",
  empty: {
    title: "No flows found",
    description: "Create a new flow to get started"
  },
  new: {
    title: "New flow",
    description: "Create a new flow"
  },
  install: "Install",
  installing: "Installing...",
  installSuccess: "Flow {{name}} installed successfully",
  comingSoon: "Coming Soon",
  uninstall: "Uninstall",
  uninstalling: "Uninstalling...",
  uninstallSuccess: "Flow {{name}} uninstalled successfully",
  confirmUninstall: "Uninstall {{name}}",
  confirmUninstallDescription: "This action is irreversible. To confirm, type the current workspace name below.",
  errors: {
    missingInfo: "Missing required information",
    invalidAgent: "Invalid or not found flow",
    installFailed: "Failed to install flow",
    uninstallFailed: "Failed to uninstall flow",
    nameNotMatch: "The name entered does not match the flow name",
    workspaceNameNotMatch: "The name entered does not match the current workspace name",
    workspaceDataError: "Error getting workspace information"
  },
  untitled: "Untitled",
  settings: {
    title: "Settings",
    description: "Configure your flow",
    type: "Type",
    unsavedChanges: "There are unpublished changes",
    updateSuccess: "Flow updated",
    updateSuccessDescription: "The flow has been updated successfully",
    saveButton: {
      label: "Save",
      saving: "Saving...",
      success: "Changes saved successfully!",
      error: "Error saving changes"
    },
    publishButton: {
      label: "Publish",
      publishing: "Publishing...",
      success: "Flow published successfully!",
      error: "Error publishing flow",
      confirmTitle: "Confirm Publication",
      confirmDescription: "Give this flow version a name and description.",
      confirm: "Yes, publish",
      cancel: "Cancel",
      versionNameLabel: "Version name",
      versionNamePlaceholder: "E.g.: Beta Version 1.0",
      versionNameError: "This name is already in use. Please choose another name.",
      versionNameRequired: "Version name is required",
      versionDescriptionLabel: "Version description",
      versionDescriptionPlaceholder: "E.g.: Improvements in external API tool",
      versionDescriptionRequired: "Version description is required",
      workflowSchemaRequired: "Workflow schema is required",
      versionAutoGenerated: "The version number is automatically generated and cannot be edited"
    },
    unsavedChangesDialog: {
      title: "Unpublished changes",
      description: "You have changes that have not been published yet. If you leave now, these changes will be lost.",
      continueEditing: "Continue Editing",
      discardAndExit: "Discard and Exit"
    },
    tabs: {
      integrations: "Tools",
      prompts: "Prompts",
      parameters: "Parameters",
      workflow: "Workflow",
      executions: "Executions",
      versions: "Versions",
      dangerZone: "Danger Zone"
    },
    dangerZoneDescription: "Dangerous actions like uninstallation can cause data loss. Proceed with caution.",
    uninstallButton: {
      label: "Uninstall this flow",
      confirmTitle: "Confirm uninstallation",
      confirmDescription: "This action is irreversible and will remove the flow from the current workspace. To confirm, type the exact name of the workspace.",
      confirmAgentNameLabel: "Workspace name",
      confirmAgentNamePlaceholder: "Type the workspace name to confirm",
      typeName: "Type the name",
      uninstalling: "Uninstalling...",
      uninstallingTitle: "Uninstalling flow",
      uninstallingProgress: "Progress: {{progress}}%",
      uninstallingDescription: "Removing {{name}} from the current workspace",
      uninstallingStep: "Uninstalling",
      uninstallingMessage: "This may take a moment, please wait...",
      uninstalledMessage: "Uninstallation complete! Redirecting...",
      confirm: "Uninstall",
      cancel: "Cancel"
    },
    systemPrompt: "System Prompt",
    systemPromptHelp: "This is the system prompt that guides the AI in the steps of this flow",
    addIntegration: "Add Tool",
    noDescription: "No description",
    status: {
      active: "Active",
      inactive: "Inactive",
      draft: "Draft"
    },
    basic: {
      name: "Name",
      description: "Description",
      type: "Type",
      version: "Version",
      status: "Status",
      active: "Active",
      inactive: "Inactive",
      draft: "Draft",
      published: "Published"
    },
    general: {
      name: "Name",
      description: "Description",
      type: "Type",
      version: "Version",
      status: "Status",
      lastPublished: "Last Published",
      noDescription: "Never published"
    },
    parameters: {
      title: "Flow Parameters",
      saveButton: "Save Parameters",
      saved: "Parameters saved successfully",
      savedSuccess: "Parameters saved successfully",
      missingRequired: "Required fields not filled",
      missingFields: "Missing required fields",
      saveError: "Error saving parameters",
      missingRequiredField: "Required field missing: {{field}}",
      noParametersTitle: "No parameters defined",
      noParametersDescription: "This flow has no configurable parameters defined.",
      enabled: "Enabled",
      disabled: "Disabled",
      required: "Required",
      types: {
        string: "Text",
        number: "Number",
        select: "Choice",
        boolean: "Yes/No",
        json: "JSON"
      }
    },
    integrations: {
      title: "Tools",
      description: "Manage your flow tools",
      add: "Add",
      remove: "Remove",
      noIntegrations: "No tools found",
      status: {
        active: "Active",
        inactive: "Inactive",
        pending: "Pending",
        error: "Error"
      },
      selectTitle: "Select Tool",
      searchPlaceholder: "Search tools...",
      selectDescription: "Choose a tool to add to your flow",
      lastUpdated: "Last Updated",
      neverUpdated: "Never Updated",
      integrationUrl: "Tool URL",
      added: "✓",
      noIntegrationsAvailable: "No available tools to add",
      noSearchResults: "No tools found for your search",
      noDescription: "No description available",
      addedSuccessfully: "Tool added successfully",
      removedSuccessfully: "Tool removed successfully",
      openai: {
        title: "OpenAI",
        description: "Tool with OpenAI for text and image generation using GPT-4 and DALL-E"
      },
      azureDevOps: {
        title: "Azure DevOps",
        description: "Integrate with Azure DevOps to manage work items, code repositories, and pipelines"
      },
      github: {
        title: "GitHub",
        description: "GitHub tool using Personal Access Token (PAT) authentication"
      },
      labels: {
        active: "Active",
        inactive: "Inactive"
      }
    },
    prompts: {
      title: "Flow Prompts",
      untitled: "Untitled",
      copy: "Copy",
      preview: "Preview",
      edit: "Edit",
      add: "Add Section",
      delete: "Delete",
      noSections: "No prompt sections configured",
      cannotDeleteLast: "Cannot delete the last prompt section",
      locked: "This prompt is locked and cannot be edited",
      cancel: "Cancel",
      systemPrompt: "System Prompt",
      systemPromptHelp: "This is the system prompt that guides the AI in the steps of this flow",
      description: "Configure your flow prompts"
    },
    fields: {
      name: "Name",
      description: "Description",
      type: "Type",
      version: "Version",
      author: "Author",
      lastPublished: "Last Published",
      status: "Status",
      logoUrl: "Logo URL",
      parameters: {
        title: "Parameters",
        description: "Configure the parameters that your flow will use",
        fields: "Fields",
        trigger: "Trigger",
        artifactType: "Artifact Type",
        parameterName: "Parameter Name",
        parameterType: "Parameter Type",
        required: "Required",
        addParameter: "Add",
        custom_field: "Custom Field",
        string: "String",
        number: "Number",
        boolean: "Boolean",
        array: "Array",
        object: "Object",
        noParameters: "No parameters configured",
        addFirstParameter: "Add your first parameter",
        deleteParameter: "Delete parameter",
        parameterNamePlaceholder: "Enter parameter name",
        parameterTypePlaceholder: "Select parameter type"
      },
      integrations: {
        title: "Tools",
        connected: "Connected",
        available: "Available",
        configure: "Configure",
        remove: "Remove"
      }
    },
    placeholders: {
      name: "Enter flow name",
      description: "Enter flow description",
      version: "Ex: 1.0.0",
      systemPrompt: "Enter system prompt for the flow",
      promptTitle: "Enter prompt title",
      promptContent: "Enter prompt content"
    },
    messages: {
      saved: "Settings saved successfully",
      error: "Error saving settings",
      promptAdded: "Prompt added successfully",
      promptUpdated: "Prompt updated successfully",
      promptDeleted: "Prompt removed successfully",
      integrationAdded: "Tool added successfully",
      integrationRemoved: "Tool removed successfully",
      titleUpdated: 'Title updated successfully',
      errorUpdatingTitle: 'Error updating title',
    },
    nodeConfig: {
      saveSuccess: "Settings saved",
      configurationUpdated: "Node configuration has been updated successfully",
      saveError: "Error saving settings",
      test: {
        success: "Test completed successfully",
        outputSaved: "Test output has been saved"
      },
      array: {
        noItemFields: "This list does not declare the fields of its items, so its entries cannot be edited here."
      },
      mapping: {
        addItem: "Add Item",
        removeItem: "Remove",
        item: "Item",
        sourceField: "Source Field",
        targetParameter: "Target Parameter",
        valueType: "Value Type",
        transformation: "Transformation",
        defaultValue: "Default Value",
        types: {
          string: "String",
          number: "Number",
          boolean: "Boolean",
          object: "Object",
          array: "Array"
        },
        transformations: {
          none: "None",
          trim: "Trim",
          lowercase: "Lowercase",
          uppercase: "Uppercase",
          capitalize: "Capitalize"
        },
        operators: {
          placeholder: 'Select operator',
          starts_with: 'starts with',
          ends_with: 'ends with',
          contains: 'contains',
          equals: 'is equal to',
          not_equals: 'is not equal to',
          greater_than: 'is greater than',
          less_than: 'is less than',
          greater_equals: 'is greater than or equal to',
          less_equals: 'is less than or equal to',
          exists: 'exists',
          not_exists: 'does not exist',
          empty: 'is empty',
          not_empty: 'is not empty',
          and: 'AND',
          or: 'OR'
        }
      }
    },
    webhook: {
      waiting_title: "Waiting for Webhook",
      waiting_message: "Waiting for webhook response...", // Renomeado para evitar duplicação
      webhook_url: "Webhook URL",
      cancel_button: "Cancel",
      timeout: "Timeout",
      timeout_description: "The webhook did not respond within the expected time",
      waiting: "Waiting for webhook response",
      waiting_description: "Please wait while the webhook is being processed",
      success: "Webhook processed successfully",
      success_description: "The webhook has been processed successfully",
      error: "Error processing webhook",
      error_description: "An error occurred while processing the webhook",
      cancel: "Cancel",
      cancel_description: "Cancel webhook processing"
    },
    versions: {
      title: "Version History",
      subtitle: "Complete history of all published versions of this flow",
      noVersions: 'No versions found',
      versionNumber: 'Version {{number}}',
      createdAt: 'Created {{time}}',
      webhookPath: "Webhook Path",
      webhookEndpoint: "Webhook Endpoint",
      triggers: "Linked Triggers",
      pathCopied: "Path copied to clipboard",
      name: 'Name',
      status: 'Status',
      description: 'Description',
      restore: {
        button: 'Restore',
        confirmTitle: 'Restore Version',
        confirmDescription: 'This will restore this version as the current flow version. The current version will be archived. Do you want to continue?',
        confirm: 'Yes, restore',
        cancel: 'Cancel',
        success: 'Version restored successfully!',
        error: 'Error restoring version'
      },
      statusLabels: {
        active: 'Active',
        inactive: 'Inactive',
        error: 'Error',
        current: 'Current',
        archived: 'Archived',
        published: 'Published',
        draft: 'Draft',
        deprecated: 'Deprecated'
      }
    }
  },
  executions: {
    title: "Executions",
    subtitle: "Flow execution history",
    id: "ID",
    status: "Status",
    startedAt: "Started at",
    completedAt: "Completed at",
    agentVersion: "Flow Version",
    noExecutions: "No executions found",
    actions: {
      viewDetails: "View details"
    },
    details: {
      title: "Execution Details",
      subtitle: "View detailed logs of node executions in this flow",
      nodeTitle: "Title",
      nodeType: "Node Type",
      startedAt: "Started At",
      completedAt: "Completed At",
      status: "Status",
      details: "Details",
      input: "Input",
      processingLog: "Processing",
      noProcessingLog: "No processing log captured for this execution.",
      downloadReport: "Download Log",
      output: "Output",
      error: "Error",
      noLogs: "No execution logs found for this flow"
    },
    status_types: {
      pending: "Pending",
      running: "Running",
      completed: "Completed",
      error: "Error",
      failed: "Failed",
      canceled: "Canceled",
      started: "Started",
      waiting: "Waiting for the agent",
      resuming: "Resuming"
    }
  },
  execution_status: {
    pending: 'Pending',
    running: 'Running',
    completed: 'Completed',
    error: 'Error',
    canceled: 'Canceled'
  },
  workflow: {
    title: "Workflow",
    description: "Configure workflow",
    save: "Save",
    saving: "Saving...",
    test: {
      title: 'Test',
      button: 'Test',
      description: 'Test this node with current configuration',
      testing: 'Testing',
    },
    testSuccessTitle: "Success",
    testSuccess: "Workflow executed successfully",
    testErrorTitle: "Error",
    testError: "Error executing workflow",
    noFields: "This workflow has no fields to configure",
    input: "Input",
    output: "Output",
    noInputTitle: "No input yet!",
    noInputDesc: "Run the previous node to see input data here",
    noOutputTitle: "No output yet!",
    noOutputDesc: "Run this node to see output data here",
    noWorkflow: "No workflow found",
    noWorkflowDescription: "This workflow has no steps configured yet.",
    unnamedNode: "Node",
    loading: "Loading workflow...",
    executing: "Executing...",
    error: "Error loading the workflow",
    new: "New Flow",
    create: "Create",
    created: "Flow created successfully",
    createError: "Error creating flow",
    updated: "Flow updated successfully",
    updateError: "Error updating flow",
    deleted: "Flow deleted successfully",
    deleteError: "Error deleting flow",
    deleteConfirm: "Are you sure you want to delete this flow?",
    nameRequired: "Name is required",
    namePlaceholder: "Enter flow name",
    select: "Select Flow",
    selectPlaceholder: "Select a flow",
    components: "Components",
    nodeConfig: {
      saveSuccess: "Node configuration saved successfully",
      configurationUpdated: "Node configuration has been updated successfully",
      saveError: "Error saving settings",
      test: {
        success: "Test completed successfully",
        outputSaved: "Test output has been saved"
      },
      mapping: {
        addItem: "Add Item",
        removeItem: "Remove",
        item: "Item",
        sourceField: "Source Field",
        targetParameter: "Target Parameter",
        valueType: "Value Type",
        transformation: "Transformation",
        defaultValue: "Default Value",
        types: {
          string: "String",
          number: "Number",
          boolean: "Boolean",
          object: "Object",
          array: "Array"
        },
        transformations: {
          none: "None",
          trim: "Trim",
          lowercase: "Lowercase",
          uppercase: "Uppercase",
          capitalize: "Capitalize"
        },
        operators: {
          placeholder: 'Select operator',
          starts_with: 'starts with',
          ends_with: 'ends with',
          contains: 'contains',
          equals: 'is equal to',
          not_equals: 'is not equal to',
          greater_than: 'is greater than',
          less_than: 'is less than',
          greater_equals: 'is greater than or equal to',
          less_equals: 'is less than or equal to',
          exists: 'exists',
          not_exists: 'does not exist',
          empty: 'is empty',
          not_empty: 'is not empty',
          and: 'AND',
          or: 'OR'
        }
      }
    },
    nodeConfigSaved: "Node configuration saved successfully",
    nodeDeleted: "Node deleted successfully",
    node: {
      config: {
        title: "Node Configuration",
        save: "Save",
        saving: "Saving...",
        test: "Test Node",
        testing: "Testing node...",
        output: "Output",
        noOutput: "Run test to see output",
        testError: "Error executing node",
        testErrorTitle: "Test Error",
        testSuccess: "Node executed successfully",
        testSuccessTitle: "Success"
      },
      menu: {
        configure: "Configure",
        delete: "Delete"
      },
      title: {
        updated: "Node title updated successfully",
        updateError: "Error updating node title"
      }
    },
    categories: {
      API: "APIs",
      Databases: "Databases",
      Files: "Files",
      Embeddings: "Embeddings",
      Triggers: "Triggers",
      Transformers: "Transformers",
      Chats: "Chats",
      "Knowledge Base": "Brain",
      Logic: "Logic",
      other: "Others",
      Service: "Services"
    },
    nodeTypes: {
      input: "Data Input",
      output: "Data Output",
      process: "Processing",
      condition: "Condition",
      loop: "Loop"
    },
    nodes: {
      API: {
        title: "APIs",
        description: "API Tool Components"
      },
      Databases: {
        title: "Databases",
        description: "Components for database tool"
      },
      Files: {
        title: "Files",
        description: "Components for file manipulation"
      },
      Embeddings: {
        title: "Embeddings",
        description: "Components for embeddings processing"
      },
      Triggers: {
        title: "Triggers",
        description: "Components for flow automation"
      },
      other: {
        title: "Others",
        description: "Other components"
      }
    },
    webhook: {
      waiting_title: "Waiting for Webhook",
      waiting_message: "Waiting for webhook response...", // Renomeado para evitar duplicação
      cancel_button: "Cancel",
      timeout: "Timeout",
      timeout_description: "The webhook did not respond in time",
      waiting: "Waiting for webhook response",
      waiting_description: "Please wait while the webhook is being processed",
      success: "Webhook processed successfully",
      success_description: "The webhook has been processed successfully",
      error: "Error processing webhook",
      error_description: "An error occurred while processing the webhook",
      cancel: "Cancel",
      cancel_description: "Cancel webhook processing"
    }
  },
  list: {
    title: "Your flows",
    description: "Manage the flows of this workspace",
    empty: "You don't have any flows yet",
    createFirst: "Create your first flow",
    create: "Create Flow",
    search: "Search flows...",
    filter: {
      all: "All",
      active: "Active",
      inactive: "Inactive",
      draft: "Drafts"
    }
  },
  form: {
    name: "Name",
    description: "Description",
    type: "Type",
    status: "Status",
    model: "Model",
    temperature: "Temperature",
    maxTokens: "Max Tokens",
    systemPrompt: "System Prompt",
    placeholders: {
      name: "Enter flow name",
      description: "Enter flow description",
      systemPrompt: "Enter system prompt for the flow"
    }
  },
  status: {
    active: "Active",
    inactive: "Inactive",
    error: "Error",
    unknown: "Unknown"
  },
  type: {
    "0": "Workspace",
    "1": "Pipeline"
  },
  actions: {
    create: "Create Flow",
    edit: "Edit Flow",
    delete: "Delete Flow",
    save: "Save Changes",
    cancel: "Cancel",
    test: "Test Flow",
    publish: "Publish",
    unpublish: "Unpublish",
    preview: "Preview",
    copy: "Copy",
    close: "Close"
  },
  messages: {
    created: "Flow created successfully",
    updated: "Flow updated successfully",
    deleted: "Flow deleted successfully",
    published: "Flow published successfully",
    unpublished: "Flow unpublished successfully",
    error: "Error processing operation"
  },
  // Workflow not published yet: nothing fires
  draft: {
    badge: "Draft",
    footer: "Draft · not published",
  },
  // Search and filters on the Flows page
  filter: {
    searchPlaceholder: "Search by name or description",
    clearSearch: "Clear search",
    all: "All",
    installed: "Installed",
    draft: "Drafts",
    available: "Available",
    trigger: "Trigger",
    clear: "Clear filters",
    noResults: "No flows match these filters.",
  },
  // Trigger on the workflow card
  trigger: {
    none: "No active trigger",
    types: {
      schedule: "Schedule",
      webhook: "Webhook",
      manual: "Manual",
      email: "Email",
      storage: "File",
      other: "Trigger",
    },
    schedule: {
      everyMinute: "every minute",
      everyNMinutes_one: "every {{count}} minute",
      everyNMinutes_other: "every {{count}} minutes",
      hourly: "hourly at minute {{minute}}",
      everyNHours_one: "every {{count}} hour",
      everyNHours_other: "every {{count}} hours",
      daily: "every day at {{time}}",
      weekdays: "weekdays at {{time}}",
      weekly: "{{days}} at {{time}}",
      monthly: "every month on day {{day}} at {{time}}",
      custom: "cron {{cron}}",
    },
    timezone: "Time zone: {{timezone}}",
  },
} as const;

export default agents;
